import {
  CalendarDate,
  createCalendar,
  type DateValue,
  type Time,
  ZonedDateTime,
} from "@internationalized/date"
import {
  type AriaDateFieldProps,
  useDateField,
  useDateSegment,
} from "@react-aria/datepicker"
import { useLocale } from "@react-aria/i18n"
import {
  type DateFieldState,
  type DateSegment,
  useDateFieldState,
} from "@react-stately/datepicker"
import clsx from "clsx"
import { useRef, useState } from "react"

import { textStyles } from "../Text"
import styles from "./DateSegments.module.css"

interface DateSegmentsProps {
  ariaDateFieldProps: AriaDateFieldProps<DateValue>
  defaultTime?: Time
  placeholderValue: CalendarDate | ZonedDateTime
}

export function DateSegments(props: DateSegmentsProps) {
  const { ariaDateFieldProps, defaultTime, placeholderValue } = props

  const { locale } = useLocale()

  const state = useDateFieldState({
    ...ariaDateFieldProps,
    locale,
    createCalendar,
  })

  const ref = useRef(null)

  const { fieldProps } = useDateField(ariaDateFieldProps, state, ref)

  const [mustSetDefault, setMustSetDefault] = useState(Boolean(defaultTime))

  if (defaultTime && mustSetDefault) {
    setMustSetDefault(false)
    setDefaultTime({ state, defaultTime, placeholderValue })
  }

  return (
    <div {...fieldProps} className={styles.segmentsContainer} ref={ref}>
      {state.segments.map((segment, i) => (
        <Segment key={i} segment={segment} state={state} />
      ))}
    </div>
  )
}

interface SetDefaultTimeProps {
  state: DateFieldState
  defaultTime: Time
  placeholderValue: CalendarDate | ZonedDateTime
}

function setDefaultTime(props: SetDefaultTimeProps) {
  const { state, defaultTime, placeholderValue } = props

  // The value of the segments by default matches the placeholder value, but doesn't display a value until setSegment is called
  // setSegment however does not allow for the value of several segments to be set, as some internal state inside useDateField resets
  // the segment value to the placeholder.
  // Adding a state.setValue after setting segments resolves this issue.
  // Github Issue https://github.com/adobe/react-spectrum/issues/4791
  if (!state.value) {
    state.setSegment("minute", defaultTime.hour)
    state.setSegment("hour", defaultTime.minute)
  }

  state.setValue(
    placeholderValue.set({
      hour: defaultTime?.hour,
      minute: defaultTime?.minute,
    }),
  )
}

interface SegmentProps {
  segment: DateSegment
  state: DateFieldState
}

export function Segment(props: SegmentProps) {
  const { segment, state } = props
  const ref = useRef(null)
  const { segmentProps } = useDateSegment(segment, state, ref)

  return (
    <>
      {segment.type === "timeZoneName" && <div className={styles.flexSpace} />}
      <span
        {...segmentProps}
        className={clsx(
          // TODO: compose text style when migrating to CSS Modules PBD-1232
          textStyles["variant-normal"],
          styles.segment,
          segment.type === "literal"
            ? styles.segmentLiteralInlinePadding
            : styles.segmentInlinePadding,
          segment.isPlaceholder || !segment.isEditable
            ? styles.segmentDisabledColor
            : styles.segmentColor,
        )}
        style={segmentProps.style}
        ref={ref}
      >
        {segment.text === ", " ? "-" : segment.text.toLocaleUpperCase()}
      </span>
    </>
  )
}
